/**
 * Baked rival routes: the train/race split made real.
 *
 * The one-shot LLM planner cannot learn a level, and the heuristic planner
 * reads geometry it has never played. Routes are the third layer: a verb
 * script authored offline (agent-deck play sessions, position traces, or the
 * level's own demo path) that the 60fps executor runs live. The LLM stays
 * for levels without routes and for recovery detours; the executor stays the
 * only thing that touches inputs. A route is knowledge, not a ghost: skill
 * tiers still pace it, deaths and rotted portals re-enter it mid-script, and
 * every step goes through the same firing and steering paths as a plan.
 *
 * Step forms (positions are world coordinates):
 *   { kind: 'fling', from: [x,y,z], to: [x,y,z] }
 *       Place the pair and ride it. `from`/`to` both select the surface the
 *       position lies on AND serve as the aim point, so a route can pin a
 *       portal near a lip or a landing line, which the heuristic's
 *       aim-at-face-centre never could.
 *   { kind: 'plunge', entry: [x,y,z], exit: [x,y,z] }
 *       A fling whose entry floor is invisible from standing ground (a pit
 *       under a lip): place the exit first, then leap toward the entry pin
 *       and fire it mid-fall, dropping straight in.
 *   { kind: 'goto', target: [x,y,z], minY }
 *       Steer there (air control included mid-launch); minY gates arrival.
 *   { kind: 'dive' }
 *       Fall back into your own entry portal (wherever it is) until a
 *       transit registers. The repeat half of every vertical-fling loop.
 *   shard: [x,y,z] on any step means "this step exists to collect that
 *   shard": once it is taken the step is skipped, which is also how a death
 *   resumes the script instead of replaying it.
 */
export const RIVAL_ROUTES = {
  // UPLINK: pit portal just past the lip (the leap crosses it), exit
  // through the deck, then ladder the pads: collect, dive back in, come out
  // faster. Terrace and gate after shard five. Mirrors the demo hologram.
  uplink: [
    // Exit pin sits east of the spawn-to-lip walking line on purpose:
    // an exit portal on your own approach path is a trapdoor.
    { kind: 'plunge', entry: [0, -78, -19], exit: [6, 0, -4] },
    { kind: 'goto', target: [14, 20, 4], minY: 17.5, shard: [14, 20, 4] },
    { kind: 'dive', shard: [-13, 32, -6] },
    { kind: 'goto', target: [-13, 32, -6], minY: 29.5, shard: [-13, 32, -6] },
    // Shard three's pad sits UNDER the terrace lip: the approach that works
    // (traced) is converging on the column DURING ascent, bonking the
    // terrace underside, and dropping onto the pad. A missed convergence
    // lands on the terrace above, fails off_target, and re-dives.
    { kind: 'dive', shard: [10, 46, -14] },
    { kind: 'goto', target: [10, 46, -14], minY: 43.5, shard: [10, 46, -14], via: 'bonk' },
    { kind: 'dive', shard: [-15, 58, 2] },
    { kind: 'goto', target: [-15, 58, 2], minY: 55.5, shard: [-15, 58, 2] },
    { kind: 'dive', shard: [4, 72, 12] },
    { kind: 'goto', target: [4, 72, 12], minY: 69.5, shard: [4, 72, 12] },
    // The endgame is a genuine choice, so the route declines to make it
    // and the brain decides with the race in front of it (RivalBrain
    // _decide). Safe catches the terrace checkpoint deck and walks the
    // gate; fast rides the same launch straight at the gate line, which
    // is quicker and lands in the pit when the drift is off.
    { kind: 'branch',
      why: 'terrace stop (safe) vs direct gate line off the last launch (fast)',
      safe: [
        { kind: 'dive' },
        { kind: 'goto', target: [16, 63, -14], minY: 61.5 },
        { kind: 'goto', target: [16, 62, -22] },
      ],
      fast: [
        { kind: 'dive' },
        { kind: 'goto', target: [16, 62, -22], minY: 60.5 },
      ] },
  ],

  // THE SPIRE: the uplink loop around a 100m central shaft. Every cycle is
  // a full plunge, then a landing goto that collects. Exit pins are NOT
  // under their ledges: the ledges overhang the deck, and a launch under
  // one bonks its own ceiling at fifteen metres (measured: 57.6 in, 58.5
  // out, zero at y 15). Every pin below has clear sky to the ~97m apex;
  // the landing goto drifts onto the ledge on the way down. The cycle also
  // stays east of the fizzler curtain (x=20.2) and out of the mover's
  // sweep lane (x -6.5..2.5 at y 42) and the terrace shadow (x -27..-9,
  // z -39..-21 at y 90).
  //
  // Authored from geometry EXCEPT the last block, which is mined: see
  // below. The whole mined line does not transfer wholesale (checked:
  // 1/6), because a human carries run-up speed through each transit and
  // rides one apex across three ledges, while the executor enters its
  // dives vertically with air control capped low. Pins mine well; arcs
  // do not.
  spire: [
    { kind: 'plunge', entry: [0, -100, 6], exit: [-31, 0, -18], shard: [-22, 20, -20] },
    { kind: 'goto', target: [-22, 19.5, -21], minY: 17.5, shard: [-22, 20, -20] },
    { kind: 'plunge', entry: [0, -100, 6], exit: [20, 0, 9], shard: [20, 32, 18] },
    { kind: 'goto', target: [20, 31.5, 18], minY: 29.5, shard: [20, 32, 18] },
    { kind: 'plunge', entry: [0, -100, 6], exit: [-26, 0, -6], shard: [-26, 48, 6] },
    { kind: 'goto', target: [-26, 47.5, 6], minY: 45.5, shard: [-26, 48, 6] },
    { kind: 'plunge', entry: [0, -100, 6], exit: [26, 0, 0], shard: [29, 60, -14] },
    { kind: 'goto', target: [28, 59.5, -14], minY: 57.5, shard: [29, 60, -14] },
    { kind: 'plunge', entry: [0, -100, 6], exit: [4, 0, 32], shard: [-8, 72, 26] },
    { kind: 'goto', target: [-8, 71.5, 27], minY: 69.5, shard: [-8, 72, 26] },
    // Ledge six, the shard geometry could not solve: a deck-start dive
    // tops out at ~97 against a 96m landing, and every authored pin
    // missed. These two pins are PILOT-421's, mined from the record run
    // (tools/ghost-route.mjs spire): the human's launch corner for the
    // tall ledge, which nothing in the level data suggested.
    { kind: 'plunge', entry: [12.9, -100, -13.7], exit: [10.8, 0, -20.4], shard: [4, 98, -22] },
    { kind: 'goto', target: [1.9, 96, -20.7], minY: 94.5, shard: [4, 98, -22] },
    { kind: 'plunge', entry: [0, -100, 6], exit: [6, 0, -30] },
    { kind: 'goto', target: [-18, 91, -28], minY: 89.5 },
    { kind: 'goto', target: [-18, 89.5, -34] },
  ],

  // VELOCITY: no portals until the drop chamber; the pads ARE the course.
  // Each landing goto walks into the next pad's trigger and the launch
  // carries the following step. The lift ride completes at altitude (air),
  // then steps off south onto the ring slab that holds pad four. The drop
  // chamber is the uplink move again: wall portal from the catwalk, pit
  // portal fired mid-fall, and the launch carries clean to the exit bay.
  velocity: [
    { kind: 'goto', target: [0, 1, -8] },
    { kind: 'goto', target: [0, 1, -70], minY: -0.5 },
    // No "stand at the lift base" step: the base is inside the field's
    // grab zone, so a grounded arrival there can never happen (traced: a
    // 30-second yo-yo). Ride until high, then step off south.
    { kind: 'goto', target: [0, 11, -136], minY: 9.5 },
    { kind: 'goto', target: [0, 41, -200], minY: 40, air: true },
    // Step off EAST (a 0.6m gap), round the ring corner with margin, and
    // walk into pad four GROUNDED; the launch flag hands off mid-flight.
    // Drifting over the pad mid-fall triggers it on an untuned arc that
    // undershoots the long haul into the void (traced: a deterministic
    // death loop every nineteen seconds), and a straight line from the
    // east slab cuts the ring corner exactly on the centre-gap edge.
    { kind: 'goto', target: [8, 45, -200], minY: 43.5 },
    { kind: 'goto', target: [6.5, 45, -189], minY: 43.5 },
    { kind: 'goto', target: [0, 45, -190], minY: 43.5, launch: true },
    { kind: 'goto', target: [0, 27, -292], minY: 25.5 },
    { kind: 'goto', target: [0, 27, -314], minY: 25.5 },
    { kind: 'plunge', entry: [0, -46, -324], exit: [9.5, 33, -308.4] },
    { kind: 'goto', target: [0, -49, -436], minY: -51.5 },
  ],

  // THE DESCENT: four stages, each a plunge (pit floor entry pinned past
  // the lip, exit pinned mid-band on the launch wall behind) and a landing
  // goto on the next terrace. Stage three's live grid crosses the walk
  // line, so its landing target and entry pin sit east of the grid's edge.
  // The last launch flies the exit bay's whole length to the gate.
  descent: [
    { kind: 'plunge', entry: [0, 178, -18], exit: [0, 251, 15] },
    { kind: 'goto', target: [0, 177, -125], minY: 175 },
    { kind: 'plunge', entry: [0, 114, -103], exit: [0, 187, -136] },
    { kind: 'goto', target: [11.5, 113, 6], minY: 111.5 },
    { kind: 'plunge', entry: [11, 50, -18], exit: [0, 123, 15] },
    { kind: 'goto', target: [0, 49, -114], minY: 47.5 },
    { kind: 'plunge', entry: [0, -14, -103], exit: [0, 59, -136] },
    { kind: 'goto', target: [0, -15, 45], minY: -17.5 },
  ],
};
